import { useEffect, useState } from "react";
import { X as XIcon, Plus, FileText, Check } from "lucide-react";
import { Button } from "../../components/UI/Button";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import Table from "../../components/UI/Table";
import { useParams } from "react-router-dom";
import { companyPricingUseCase } from "di/container";
import type {
  WasteRateSetting,
  WasteRateSettingRequest,
} from "domain/types/pricing";

// 손지율 템플릿 정의
const WASTE_RATE_TEMPLATES = [
  {
    id: "standard",
    name: "표준 템플릿",
    description: "일반적인 인쇄 작업에 적합한 손지율",
    rates: [
      { maxQuantity: 100, color1: 15, color1_1: 18, color2: 20, color2_2: 22, color3: 24, color3_3: 26, color4: 28, color4_4: 30 },
      { maxQuantity: 500, color1: 10, color1_1: 12, color2: 14, color2_2: 16, color3: 18, color3_3: 20, color4: 22, color4_4: 24 },
      { maxQuantity: 1000, color1: 7, color1_1: 9, color2: 11, color2_2: 13, color3: 15, color3_3: 17, color4: 19, color4_4: 21 },
      { maxQuantity: 3000, color1: 5, color1_1: 7, color2: 9, color2_2: 11, color3: 13, color3_3: 15, color4: 17, color4_4: 19 },
      { maxQuantity: 5000, color1: 4, color1_1: 6, color2: 8, color2_2: 10, color3: 12, color3_3: 14, color4: 16, color4_4: 18 },
      { maxQuantity: null, color1: 3, color1_1: 5, color2: 7, color2_2: 9, color3: 11, color3_3: 13, color4: 15, color4_4: 17 },
    ],
  },
  {
    id: "simple",
    name: "간편 템플릿",
    description: "3단계 구간의 간단한 손지율",
    rates: [
      { maxQuantity: 500, color1: 12, color1_1: 14, color2: 16, color2_2: 18, color3: 20, color3_3: 22, color4: 24, color4_4: 26 },
      { maxQuantity: 2000, color1: 7, color1_1: 9, color2: 11, color2_2: 13, color3: 15, color3_3: 17, color4: 19, color4_4: 21 },
      { maxQuantity: null, color1: 4, color1_1: 6, color2: 8, color2_2: 10, color3: 12, color3_3: 14, color4: 16, color4_4: 18 },
    ],
  },
];

const WasteRateManagementPage: React.FC = () => {
  const { companyId } = useParams();
  const cid = Number(companyId);

  const [wasteRates, setWasteRates] = useState<WasteRateSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedRate, setSelectedRate] = useState<WasteRateSetting | null>(null);
  const [formData, setFormData] = useState<WasteRateSettingRequest>({
    maxQuantity: null,
    color1: 0,
    color1_1: 0,
    color2: 0,
    color2_2: 0,
    color3: 0,
    color3_3: 0,
    color4: 0,
    color4_4: 0,
  });

  const fetchWasteRates = async () => {
    if (!Number.isFinite(cid)) return;
    try {
      setIsLoading(true);
      const result = await companyPricingUseCase.getWasteRates(cid);
      setWasteRates(result);
    } catch (e) {
      console.error(e);
      alert("손지율 설정을 불러오지 못했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWasteRates();
  }, [companyId]);

  const handleCreate = async () => {
    try {
      await companyPricingUseCase.createWasteRate(cid, formData);
      await fetchWasteRates();
      setIsAddModalOpen(false);
      resetFormData();
    } catch (e) {
      console.error(e);
      alert("손지율 설정 추가에 실패했습니다.");
    }
  };

  const handleUpdate = async () => {
    if (!selectedRate) return;
    try {
      await companyPricingUseCase.updateWasteRate(cid, selectedRate.id, formData);
      await fetchWasteRates();
      setIsEditModalOpen(false);
      setSelectedRate(null);
      resetFormData();
    } catch (e) {
      console.error(e);
      alert("손지율 설정 수정에 실패했습니다.");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      await companyPricingUseCase.deleteWasteRate(cid, id);
      await fetchWasteRates();
    } catch (e) {
      console.error(e);
      alert("삭제에 실패했습니다.");
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;

    const template = WASTE_RATE_TEMPLATES.find((t) => t.id === selectedTemplate);
    if (!template) return;

    try {
      for (const rate of template.rates) {
        await companyPricingUseCase.createWasteRate(cid, rate);
      }
      await fetchWasteRates();
      setIsTemplateModalOpen(false);
      setSelectedTemplate(null);
    } catch (e) {
      console.error(e);
      alert("템플릿 적용에 실패했습니다.");
    }
  };

  const resetFormData = () => {
    setFormData({
      maxQuantity: null,
      color1: 0,
      color1_1: 0,
      color2: 0,
      color2_2: 0,
      color3: 0,
      color3_3: 0,
      color4: 0,
      color4_4: 0,
    });
  };

  const openEditModal = (rate: WasteRateSetting) => {
    setSelectedRate(rate);
    setFormData({
      maxQuantity: rate.maxQuantity,
      color1: rate.color1,
      color1_1: rate.color1_1,
      color2: rate.color2,
      color2_2: rate.color2_2,
      color3: rate.color3,
      color3_3: rate.color3_3,
      color4: rate.color4,
      color4_4: rate.color4_4,
    });
    setIsEditModalOpen(true);
  };

  const formatQuantityRange = (sortedRates: WasteRateSetting[], index: number) => {
    const rate = sortedRates[index];
    const prevMax = index === 0 ? 0 : sortedRates[index - 1].maxQuantity;
    const min = prevMax !== null ? prevMax + 1 : 0;

    if (rate.maxQuantity === null) {
      return `${min.toLocaleString()}부 이상`;
    }
    return `${min.toLocaleString()} ~ ${rate.maxQuantity.toLocaleString()}부`;
  };

  // 템플릿 선택 모달
  const TemplateModal = () => {
    if (!isTemplateModalOpen) return null;

    return (
      <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/50">
        <div className="w-[540px] rounded-[12px] bg-white shadow-xl overflow-hidden">
          {/* 헤더 */}
          <div className="px-6 py-5 border-b border-[#e0e0e0]">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-[18px] font-semibold text-[#222]">템플릿으로 시작하기</h3>
                <p className="text-[13px] text-[#888] mt-1">
                  미리 설정된 손지율 템플릿을 선택하세요
                </p>
              </div>
              <button
                onClick={() => {
                  setIsTemplateModalOpen(false);
                  setSelectedTemplate(null);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f5f5f5] transition-colors"
              >
                <XIcon size={20} className="text-[#888]" />
              </button>
            </div>
          </div>

          {/* 템플릿 목록 */}
          <div className="p-6 space-y-3 max-h-[400px] overflow-y-auto">
            {WASTE_RATE_TEMPLATES.map((template) => (
              <div
                key={template.id}
                onClick={() => setSelectedTemplate(template.id)}
                className={`p-4 rounded-[10px] border-2 cursor-pointer transition-all ${
                  selectedTemplate === template.id
                    ? "border-[#4285F4] bg-[#F0F6FF]"
                    : "border-[#e0e0e0] hover:border-[#bbb] bg-white"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <FileText size={18} className={selectedTemplate === template.id ? "text-[#4285F4]" : "text-[#888]"} />
                      <span className={`font-medium ${selectedTemplate === template.id ? "text-[#4285F4]" : "text-[#222]"}`}>
                        {template.name}
                      </span>
                    </div>
                    <p className="text-[13px] text-[#888] mt-1 ml-[26px]">
                      {template.description}
                    </p>
                    <p className="text-[12px] text-[#aaa] mt-2 ml-[26px]">
                      {template.rates.length}개 구간
                    </p>
                  </div>
                  {selectedTemplate === template.id && (
                    <div className="w-6 h-6 rounded-full bg-[#4285F4] flex items-center justify-center">
                      <Check size={14} className="text-white" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* 푸터 */}
          <div className="px-6 py-4 border-t border-[#e0e0e0] bg-[#fafafa] flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsTemplateModalOpen(false);
                setSelectedTemplate(null);
              }}
              className="h-10 px-5 text-[14px] border-[#d0d0d0]"
            >
              취소
            </Button>
            <Button
              onClick={handleApplyTemplate}
              disabled={!selectedTemplate}
              className={`h-10 px-5 text-[14px] ${
                selectedTemplate
                  ? "bg-[#222] text-white hover:bg-[#333]"
                  : "bg-[#e0e0e0] text-[#aaa] cursor-not-allowed"
              }`}
            >
              템플릿 적용
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // 손지율 추가/수정 모달
  const WasteRateFormModal = ({
    isOpen,
    onClose,
    onSubmit,
    title,
    submitText,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: () => void;
    title: string;
    submitText: string;
  }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/50">
        <div className="w-[540px] rounded-[12px] bg-white shadow-xl overflow-hidden">
          {/* 헤더 */}
          <div className="px-6 py-5 border-b border-[#e0e0e0]">
            <div className="flex items-center justify-between">
              <h3 className="text-[18px] font-semibold text-[#222]">{title}</h3>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f5f5f5] transition-colors"
              >
                <XIcon size={20} className="text-[#888]" />
              </button>
            </div>
          </div>

          {/* 본문 */}
          <div className="p-6 space-y-5">
            {/* 수량 구간 */}
            <div>
              <label className="block text-[14px] font-medium text-[#222] mb-2">
                최대 수량
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.maxQuantity ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxQuantity: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                  className="w-full h-11 rounded-[8px] border border-[#e0e0e0] px-4 text-[14px] focus:border-[#4285F4] focus:outline-none focus:ring-2 focus:ring-[#4285F4]/20 transition-all"
                  placeholder="비워두면 무제한"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] text-[#888]">부</span>
              </div>
              <p className="mt-2 text-[12px] text-[#888]">
                최소 수량은 이전 구간의 최대 수량 + 1로 자동 계산됩니다.
              </p>
            </div>

            {/* 손지율 입력 */}
            <div>
              <label className="block text-[14px] font-medium text-[#222] mb-2">
                색상별 손지율 (%)
              </label>
              <p className="text-[12px] text-[#888] mb-3">
                도수: 사용되는 색상 수 (4색 = CMYK 풀컬러) · 앞/뒤: 앞면/뒷면 인쇄 도수
              </p>
              <div className="rounded-[10px] border border-[#e0e0e0] overflow-hidden">
                <div className="grid grid-cols-4">
                  {[
                    { key: "color1", label: "1색", desc: "단면 1도" },
                    { key: "color1_1", label: "1/1색", desc: "양면 1도" },
                    { key: "color2", label: "2색", desc: "단면 2도" },
                    { key: "color2_2", label: "2/2색", desc: "양면 2도" },
                  ].map((item, idx) => (
                    <div
                      key={item.key}
                      className={`p-3 ${idx < 3 ? "border-r border-[#e0e0e0]" : ""} bg-[#fafafa]`}
                    >
                      <label className="block text-[12px] text-[#555] font-medium mb-0.5 text-center">
                        {item.label}
                      </label>
                      <span className="block text-[10px] text-[#aaa] mb-1.5 text-center">
                        {item.desc}
                      </span>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          value={formData[item.key as keyof WasteRateSettingRequest] as number}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              [item.key]: Number(e.target.value),
                            })
                          }
                          className="w-full h-9 rounded-[6px] border border-[#e0e0e0] px-2 pr-6 text-[13px] text-center focus:border-[#4285F4] focus:outline-none bg-white"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#aaa]">%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-4 border-t border-[#e0e0e0]">
                  {[
                    { key: "color3", label: "3색", desc: "단면 3도" },
                    { key: "color3_3", label: "3/3색", desc: "양면 3도" },
                    { key: "color4", label: "4색", desc: "단면 풀컬러" },
                    { key: "color4_4", label: "4/4색", desc: "양면 풀컬러" },
                  ].map((item, idx) => (
                    <div
                      key={item.key}
                      className={`p-3 ${idx < 3 ? "border-r border-[#e0e0e0]" : ""} bg-[#fafafa]`}
                    >
                      <label className="block text-[12px] text-[#555] font-medium mb-0.5 text-center">
                        {item.label}
                      </label>
                      <span className="block text-[10px] text-[#aaa] mb-1.5 text-center">
                        {item.desc}
                      </span>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          value={formData[item.key as keyof WasteRateSettingRequest] as number}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              [item.key]: Number(e.target.value),
                            })
                          }
                          className="w-full h-9 rounded-[6px] border border-[#e0e0e0] px-2 pr-6 text-[13px] text-center focus:border-[#4285F4] focus:outline-none bg-white"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#aaa]">%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 푸터 */}
          <div className="px-6 py-4 border-t border-[#e0e0e0] bg-[#fafafa] flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="h-10 px-5 text-[14px] border-[#d0d0d0]"
            >
              취소
            </Button>
            <Button
              onClick={onSubmit}
              className="h-10 px-5 text-[14px] bg-[#222] text-white hover:bg-[#333]"
            >
              {submitText}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <CompanyAdminLayout>
      <div className="p-5">
        {/* 헤더 */}
        <div className="mb-6">
          <h2 className="text-[22px] font-semibold text-[#222] text-left">
            손지율 비용 관리
          </h2>
        </div>

        {/* 상단 액션 바 */}
        <div className="mb-5 p-5 border border-[#e0e0e0] rounded-[10px] bg-white flex flex-col sm:flex-row gap-3 justify-between items-center">
          <div className="text-[14px] text-[#555]">
            총{" "}
            <span className="font-semibold text-[#4285F4]">
              {wasteRates.length}
            </span>
            개의 구간이 설정되어 있습니다.
          </div>
          <div className="flex gap-2">
            {wasteRates.length === 0 && (
              <Button
                variant="outline"
                onClick={() => setIsTemplateModalOpen(true)}
                className="h-10 px-4 text-[14px] border-[#d0d0d0] flex items-center gap-2"
              >
                <FileText size={16} />
                템플릿으로 시작개의 구간이 설정되어 있습니다.
              </Button>
            )}
            <Button
              onClick={() => {
                resetFormData();
                setIsAddModalOpen(true);
              }}
              className="h-10 px-4 text-[14px] bg-[#222] text-white hover:bg-[#333] flex items-center gap-2"
            >
              <Plus size={16} />
              구간 추가
            </Button>
          </div>
        </div>

        {/* 컨텐츠 영역 */}
        {isLoading ? (
          <div className="text-center py-16 text-[#888]">로딩 중...</div>
        ) : wasteRates.length === 0 ? (
          <div className="py-16 bg-white rounded-[10px] border border-[#e0e0e0]">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#f5f5f5] flex items-center justify-center">
                <FileText size={28} className="text-[#bbb]" />
              </div>
              <p className="text-[15px] text-[#888] mb-6">
                설정된 손지율이 없습니다.
                <br />
                템플릿으로 빠르게 시작하거나 직접 추가해보세요.
              </p>
              <div className="flex justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setIsTemplateModalOpen(true)}
                  className="h-10 px-5 text-[14px] border-[#d0d0d0] flex items-center gap-2"
                >
                  <FileText size={16} />
                  템플릿으로 시작
                </Button>
                <Button
                  onClick={() => {
                    resetFormData();
                    setIsAddModalOpen(true);
                  }}
                  className="h-10 px-5 text-[14px] bg-[#222] text-white hover:bg-[#333] flex items-center gap-2"
                >
                  <Plus size={16} />
                  직접 추가
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-[12px]">
            <Table
              columnWidths={[
                "140px",
                "70px",
                "70px",
                "70px",
                "70px",
                "70px",
                "70px",
                "70px",
                "70px",
                "160px",
              ]}
              tabledata={{
                head: [
                  <div className="text-left">수량 구간</div>,
                  <div className="text-center">
                    <div>1색</div>
                    <div className="text-[10px] text-[#888] font-normal">
                      단면 1도
                    </div>
                  </div>,
                  <div className="text-center">
                    <div>1/1색</div>
                    <div className="text-[10px] text-[#888] font-normal">
                      양면 1도
                    </div>
                  </div>,
                  <div className="text-center">
                    <div>2색</div>
                    <div className="text-[10px] text-[#888] font-normal">
                      단면 2도
                    </div>
                  </div>,
                  <div className="text-center">
                    <div>2/2색</div>
                    <div className="text-[10px] text-[#888] font-normal">
                      양면 2도
                    </div>
                  </div>,
                  <div className="text-center">
                    <div>3색</div>
                    <div className="text-[10px] text-[#888] font-normal">
                      단면 3도
                    </div>
                  </div>,
                  <div className="text-center">
                    <div>3/3색</div>
                    <div className="text-[10px] text-[#888] font-normal">
                      양면 3도
                    </div>
                  </div>,
                  <div className="text-center">
                    <div>4색</div>
                    <div className="text-[10px] text-[#888] font-normal">
                      단면 풀컬러
                    </div>
                  </div>,
                  <div className="text-center">
                    <div>4/4색</div>
                    <div className="text-[10px] text-[#888] font-normal">
                      양면 풀컬러
                    </div>
                  </div>,
                  <div className="text-center">작업</div>,
                ],
                body: wasteRates
                  .sort((a, b) => {
                    if (a.maxQuantity === null) return 1;
                    if (b.maxQuantity === null) return -1;
                    return a.maxQuantity - b.maxQuantity;
                  })
                  .map((rate, index, sortedArray) => [
                    <div className="text-left">
                      {formatQuantityRange(sortedArray, index)}
                    </div>,
                    <div className="text-center">{rate.color1}%</div>,
                    <div className="text-center">{rate.color1_1}%</div>,
                    <div className="text-center">{rate.color2}%</div>,
                    <div className="text-center">{rate.color2_2}%</div>,
                    <div className="text-center">{rate.color3}%</div>,
                    <div className="text-center">{rate.color3_3}%</div>,
                    <div className="text-center">{rate.color4}%</div>,
                    <div className="text-center">{rate.color4_4}%</div>,
                    <div className="w-full flex gap-1.5 justify-center">
                      <Button
                        variant="outline"
                        onClick={() => openEditModal(rate)}
                      >
                        수정
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDelete(rate.id)}
                      >
                        삭제
                      </Button>
                    </div>,
                  ]),
              }}
            />
          </div>
        )}

        {/* 모달들 */}
        <TemplateModal />

        <WasteRateFormModal
          isOpen={isAddModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
            resetFormData();
          }}
          onSubmit={handleCreate}
          title="손지율 구간 추가"
          submitText="추가"
        />

        <WasteRateFormModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedRate(null);
            resetFormData();
          }}
          onSubmit={handleUpdate}
          title="손지율 구간 수정"
          submitText="수정"
        />
      </div>
    </CompanyAdminLayout>
  );
};

export default WasteRateManagementPage;
